import React, { useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Paper, Typography, useTheme, alpha } from "@mui/material";
import {
  MdToday,
  MdPendingActions,
  MdEvent,
  MdAutorenew,
  MdArrowForward,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { selectCurrentUser } from "store/features/auth/authSelectors";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import { fetchTasks } from "store/features/company/projectManagement/task/taskActions";

// Helper to check if the task belongs to the current user
const isMyTask = (task, userId, employeeId, userName) => {
  if (!userId && !employeeId && !userName) return true;

  if (task.assigned_to !== undefined && task.assigned_to !== null) {
    if (Array.isArray(task.assigned_to)) {
      if (
        task.assigned_to.some(
          (id) =>
            String(id) === String(userId) || String(id) === String(employeeId),
        )
      )
        return true;
    } else if (typeof task.assigned_to === "object") {
      if (
        String(task.assigned_to.id) === String(userId) ||
        String(task.assigned_to.id) === String(employeeId) ||
        (task.assigned_to.name &&
          userName &&
          task.assigned_to.name.toLowerCase() === userName.toLowerCase())
      )
        return true;
    } else {
      const assignedStr = String(task.assigned_to);
      if (assignedStr.includes(",")) {
        const ids = assignedStr.split(",").map((s) => s.trim());
        if (
          ids.some(
            (id) =>
              id === String(userId) ||
              id === String(employeeId) ||
              (userName && id.toLowerCase() === userName.toLowerCase()),
          )
        )
          return true;
      } else if (
        assignedStr === String(userId) ||
        assignedStr === String(employeeId) ||
        (userName && assignedStr.toLowerCase() === userName.toLowerCase())
      ) {
        return true;
      }
    }
  }

  if (Array.isArray(task.assignees) && task.assignees.length > 0) {
    if (
      task.assignees.some(
        (a) =>
          String(a.employee_id || a.user_id || a.id) === String(userId) ||
          String(a.employee_id || a.user_id || a.id) === String(employeeId),
      )
    )
      return true;
  }

  if (
    task.created_by &&
    (String(task.created_by) === String(userId) ||
      String(task.created_by) === String(employeeId))
  )
    return true;
  if (
    task.user_id &&
    (String(task.user_id) === String(userId) ||
      String(task.user_id) === String(employeeId))
  )
    return true;

  return false;
};

const isSameDay = (d1, d2) =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

const TaskStatsWidget = ({ twoPerRow = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);

  // Access the tasks array directly from the Redux state
  const tasks = useSelector((state) => state.companyTask?.tasks || []);

  // Fetch only my tasks for the last 7 days
  useEffect(() => {
    dispatch(fetchTasks({ date_range: "7d", assigned_to_me: true })).catch(
      () => {},
    );
  }, [dispatch]);

  const stats = useMemo(() => {
    const userRaw = getSecureLocalItem("user");
    const storedUser = userRaw
      ? typeof userRaw === "string"
        ? JSON.parse(userRaw)
        : userRaw
      : null;
    const user = storedUser || currentUser;

    const userId = user?.id || user?.user_id;
    const employeeId = user?.employee_id || user?.id;
    const userName =
      user?.name ||
      (user?.first_name
        ? `${user.first_name} ${user.last_name || ""}`.trim()
        : null);

    let list = Array.isArray(tasks) ? tasks : [];

    // 1. Filter out parent tasks
    list = list.filter(
      (t) =>
        t.is_parent_task === 0 ||
        t.is_parent_task === null ||
        t.is_parent_task === undefined,
    );

    // 2. Filter to only show tasks assigned to this user
    list = list.filter((task) => isMyTask(task, userId, employeeId, userName));

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let todayCount = 0;
    let pendingCount = 0;
    let upcomingCount = 0;
    let inProgressCount = 0;

    list.forEach((t) => {
      const status = String(t.status || "").toLowerCase();
      const isDone = status === "done" || status === "completed";
      const isCancelled = status === "cancelled";
      const isInProgress = status === "in_progress";
      const isPaused = status === "paused";

      const startDate = t.start_date ? new Date(t.start_date) : null;
      if (startDate) startDate.setHours(0, 0, 0, 0);

      const dueDate = t.due_date ? new Date(t.due_date) : null;
      if (dueDate) dueDate.setHours(0, 0, 0, 0);

      // 1. IN PROGRESS: Anything currently being worked on
      if (isInProgress) {
        inProgressCount++;
        return;
      }

      // Ignore done/cancelled for the remaining buckets
      if (isDone || isCancelled) return;

      // 2. UPCOMING TASKS: Starts strictly in the future
      if (startDate && startDate > today) {
        upcomingCount++;
        return;
      }

      // 3. TODAY'S TASKS: Any task that has today's date in start_date or due_date
      const isTodayStart = startDate && isSameDay(startDate, today);
      const isTodayDue = dueDate && isSameDay(dueDate, today);

      if (isTodayStart || isTodayDue) {
        todayCount++;
        return;
      }

      // 4. PENDING TASKS: Not done, not cancelled, not in progress, not paused, and start_date is today or in the past
      const isStarted = startDate && startDate <= today;
      if (!isPaused && isStarted) {
        pendingCount++;
      }
    });

    return [
      {
        key: "today",
        label: "Today's Tasks",
        value: todayCount,
        icon: <MdToday size={24} />,
        bg: "#BE123C",
        border: "#BE123C",
        iconBg: "rgba(255,255,255,0.18)",
        textColor: "#FFFFFF",
        valueColor: "#FFFFFF",
      },
      {
        key: "pending",
        label: "Pending Tasks",
        value: pendingCount,
        icon: <MdPendingActions size={24} />,
        bg: "#C2410C",
        border: "#C2410C",
        iconBg: "rgba(255,255,255,0.18)",
        textColor: "#FFFFFF",
        valueColor: "#FFFFFF",
      },
      {
        key: "upcoming",
        label: "Upcoming Tasks",
        value: upcomingCount,
        icon: <MdEvent size={24} />,
        bg: "#6D28D9",
        border: "#6D28D9",
        iconBg: "rgba(255,255,255,0.18)",
        textColor: "#FFFFFF",
        valueColor: "#FFFFFF",
      },
      {
        key: "progress",
        label: "In Progress",
        value: inProgressCount,
        icon: <MdAutorenew size={24} />,
        bg: "#047857",
        border: "#047857",
        iconBg: "rgba(255,255,255,0.18)",
        textColor: "#FFFFFF",
        valueColor: "#FFFFFF",
      },
    ];
  }, [tasks, currentUser, isDark]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: twoPerRow
          ? "repeat(2, minmax(0, 1fr))"
          : {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(4, minmax(0, 1fr))",
            },
        gap: 1.5,
        width: "100%",
        boxSizing: "border-box",
        flex: 1,
        height: "100%",
      }}
    >
      {stats.map((s) => (
        <Paper
          key={s.key}
          elevation={0}
          onClick={() => navigate("/company/tasks")}
          sx={{
            p: 2,
            borderRadius: "8px",
            bgcolor: s.bg,
            border: `1px solid ${s.border}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            minWidth: 0,
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: `0 4px 12px ${alpha(s.valueColor, 0.15)}`,
              borderColor: s.valueColor,
            },
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "8px",
              bgcolor: s.iconBg,
              color: s.textColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {s.icon}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: s.textColor,
                fontSize: "0.85rem",
                display: "block",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                mb: 0.5,
              }}
            >
              {s.label}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  fontSize: "1.5rem",
                  lineHeight: 1,
                  color: s.valueColor,
                }}
              >
                {s.value}
              </Typography>
              <MdArrowForward
                size={18}
                color={s.textColor}
                style={{ opacity: 0.5 }}
              />
            </Box>
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export default TaskStatsWidget;
